// Custom domain for a portal (Cloudflare for SaaS custom hostname): connect,
// show the DNS records the client must create, refresh status, remove.

import { useState } from 'react'
import { Link as RouterLink } from 'react-router-dom'
import {
    Alert,
    Box,
    Button,
    Chip,
    Link,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from '@mui/material'
import { Refresh } from '@mui/icons-material'
import { DASHBOARD_LINKS } from '@yourownmetric/shared'

import {
    useDeletePortalDomainMutation,
    useRefreshPortalDomainMutation,
    useSetPortalDomainMutation,
} from '@/apis/v2Api'
import { domainStatusColor, type Portal } from '@/apis/portalTypes'
import { ConfirmDialog } from '@/components/shared/ConfirmDialog'
import { CopyButton } from '@/components/shared/CopyButton'
import { useProjectId } from '@/hooks/useReportArgs'
import { SectionCard } from '@/modules/reports/common'

import { ApiErrorAlert } from './ApiErrorAlert'

export function PortalDomainCard({ portal, allowed }: { portal: Portal; allowed: boolean }) {
    const projectId = useProjectId()
    const [hostname, setHostname] = useState('')
    const [confirmRemove, setConfirmRemove] = useState(false)
    const [setDomain, setState] = useSetPortalDomainMutation()
    const [refresh, refreshState] = useRefreshPortalDomainMutation()
    const [remove, removeState] = useDeletePortalDomainMutation()
    const d = portal.custom_domain
    const error = setState.error ?? refreshState.error ?? removeState.error

    return (
        <SectionCard
            title="Custom domain"
            action={
                d && (
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <Chip
                            size="small"
                            label={d.status ?? 'unknown'}
                            color={domainStatusColor(d.status)}
                        />
                        {d.sslStatus && (
                            <Chip
                                size="small"
                                variant="outlined"
                                label={`SSL: ${d.sslStatus}`}
                                color={domainStatusColor(d.sslStatus)}
                            />
                        )}
                    </Box>
                )
            }
        >
            <ApiErrorAlert error={error} sx={{ mb: 2 }} />
            {!d ? (
                allowed ? (
                    <Box
                        component="form"
                        onSubmit={e => {
                            e.preventDefault()
                            void setDomain({ projectId, id: portal.id, hostname: hostname.trim() })
                                .unwrap()
                                .then(() => setHostname(''))
                                .catch(() => undefined)
                        }}
                        sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', flexWrap: 'wrap' }}
                    >
                        <TextField
                            size="small"
                            label="Hostname"
                            placeholder="reports.example.com"
                            value={hostname}
                            onChange={e => setHostname(e.target.value)}
                            helperText="A subdomain your client controls; they add the DNS records shown next."
                            sx={{ flex: 1, minWidth: 260 }}
                        />
                        <Button
                            type="submit"
                            variant="contained"
                            disabled={!hostname.trim() || setState.isLoading}
                        >
                            Connect
                        </Button>
                    </Box>
                ) : (
                    <Alert severity="info">
                        Serve this portal on your own domain (e.g. reports.example.com) on a paid
                        plan.{' '}
                        <Link component={RouterLink} to={DASHBOARD_LINKS.billing}>
                            View plans
                        </Link>
                    </Alert>
                )
            ) : (
                <>
                    <Typography variant="body1" fontFamily="monospace" sx={{ mb: 1 }}>
                        {d.hostname}
                    </Typography>
                    {d.status === 'active' ? (
                        <Alert severity="success" sx={{ mb: 2 }}>
                            Live at{' '}
                            <Link href={`https://${d.hostname}/`} target="_blank" rel="noopener">
                                https://{d.hostname}/
                            </Link>
                        </Alert>
                    ) : d.status === 'manual' ? (
                        <Alert severity="info" sx={{ mb: 2 }}>
                            Automatic certificates aren't configured on this server. Create the DNS
                            records below; the domain is served once they resolve and a certificate
                            is in place.
                        </Alert>
                    ) : (
                        <Alert severity="warning" sx={{ mb: 2 }}>
                            Waiting for DNS verification and the certificate. Ask your client to
                            create these records, then refresh — it can take a few minutes after DNS
                            propagates.
                        </Alert>
                    )}
                    {(d.records ?? []).length > 0 && (
                        <Table size="small" sx={{ mb: 2 }}>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Type</TableCell>
                                    <TableCell>Name</TableCell>
                                    <TableCell>Value</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {(d.records ?? []).map(r => (
                                    <TableRow key={`${r.type}:${r.name}:${r.value}`}>
                                        <TableCell>{r.type}</TableCell>
                                        <TableCell
                                            sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}
                                        >
                                            {r.name}
                                            <CopyButton text={r.name} />
                                        </TableCell>
                                        <TableCell
                                            sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}
                                        >
                                            {r.value}
                                            <CopyButton text={r.value} />
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    )}
                    {(d.errors ?? []).map(e => (
                        <Alert key={e} severity="error" sx={{ mb: 1 }}>
                            {e}
                        </Alert>
                    ))}
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button
                            startIcon={<Refresh />}
                            disabled={refreshState.isLoading}
                            onClick={() => {
                                void refresh({ projectId, id: portal.id })
                            }}
                        >
                            Refresh status
                        </Button>
                        <Button
                            color="error"
                            disabled={removeState.isLoading}
                            onClick={() => setConfirmRemove(true)}
                        >
                            Remove domain
                        </Button>
                    </Box>
                </>
            )}
            <ConfirmDialog
                open={confirmRemove}
                title="Remove custom domain?"
                message={`${d?.hostname ?? ''} stops serving this portal. Clients can still sign in at the portal's default address.`}
                confirmLabel="Remove"
                confirmColor="error"
                onConfirm={() => {
                    setConfirmRemove(false)
                    void remove({ projectId, id: portal.id })
                }}
                onCancel={() => setConfirmRemove(false)}
            />
        </SectionCard>
    )
}
